import { createAsyncThunk } from '@reduxjs/toolkit';

// functions
import { apiPrefix } from '../utils/functions';
import { fetchComputeJobs } from '../middleware/api/compute_job_apis';

// types
import { DataItemType, Project } from '../utils/types';
import { MoveDataToStorage } from '../utils/events';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from './store';

// import state definition and functions.
import {
  DataState,
  rD_setComputeJobs,
  rD_setComputeJobsLoading,
  rD_setProjects,
  rD_setServiceTypes,
  rD_setSites,
  rD_setStorageAreas,
  rD_updateRucioLocations,
} from './data-slice';

import { setLoadingProjects, setLoadingSites } from '../app-slice';

import { rG_setProject, rG_setSite } from './global-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { RucioLocationsType, Site, SiteStorageArea, StorageArea } from '../utils/types';

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  F U N C T I O N S
//
//  --------------------------------------------------------------------------

//  -------------------------------------------------
//
//  builds a list of namespaces & filenames, and
//  a list of sites where that file is staged.
//
//  -------------------------------------------------

export function fnD_buildRucioLocations(args: {
  items: DataItemType[];
  catalog: string;
  refresh?: boolean;
}) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  var updatedItems: {
    namespace: string;
    filename: string;
    refresh?: boolean | null;
    availableSites?: Site[];
    movingSites?: Site[];
    refreshingLocations?: boolean;
  }[] = [];

  // loop over the selected rows.
  for (var row: number = 0; row < args.items.length; row++) {
    //
    const item: {
      namespace: string;
      filename: string;
    } = args.items[row];

    // ensure this file is not already in the list.
    if (
      updatedItems.findIndex(
        (findItem) =>
          findItem.namespace === item.namespace && findItem.filename === item.filename,
      ) === -1
    ) {
      //
      const rucioLocationsIndex: number = stateData.rucioLocations.findIndex(
        (findRucioItem) =>
          findRucioItem.namespace === item.namespace &&
          findRucioItem.filename === item.filename,
      );

      // get the sites where this file is located.
      var availableSites: Site[] =
        rucioLocationsIndex > -1
          ? Object.assign(
              [],
              stateData.rucioLocations[rucioLocationsIndex].availableSites,
            )
          : [];

      // get the sites where this file is currently being moved to.
      var movingSites: Site[] =
        rucioLocationsIndex > -1
          ? Object.assign([], stateData.rucioLocations[rucioLocationsIndex].movingSites)
          : [];

      // remove any items from the moving-site list if they are already in the
      // available sites list.
      for (var i: number = movingSites.length - 1; i >= 0; i--) {
        if (
          availableSites.findIndex(
            (find) => find.site_uuid === movingSites[i].site_uuid,
          ) > -1
        ) {
          movingSites.splice(i, 1);
        }
      }

      // add a new item.
      var newRow: {
        namespace: string;
        filename: string;
        refresh?: boolean | null;
        availableSites?: Site[];
        movingSites?: Site[];
        refreshingLocations?: boolean;
      } = {
        namespace: item.namespace,
        filename: item.filename,
        availableSites: availableSites,
        movingSites: movingSites,
        refresh: rucioLocationsIndex === -1 && args.refresh !== false,
        refreshingLocations:
          rucioLocationsIndex > -1
            ? stateData.rucioLocations[rucioLocationsIndex].refreshingLocations
            : false,
      };

      updatedItems.push(newRow);
      //
    }
    //
  }

  // update state.
  store.dispatch(
    rD_updateRucioLocations({
      items: updatedItems,
      catalog: args.catalog,
      clearCatalog: true, // clear any existing data for this catalog because we are rebuilding.
    }),
  );
  //
} // fnD_buildRucioLocations

//  ------------------------------------------------------------
//
//  Count the number of data items that are available at the
//  supplied site.
//
//  ------------------------------------------------------------

export function fnD_countAvailableFiles(args: { site: Site; items: DataItemType[] }) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // filter these items to remove those that are not available at the site.
  let items: DataItemType[] = args.items.filter((itemFilter) => {
    //
    // find the corresponding item in the Rucio locations object.
    let siteAvailable: boolean = false;
    const itemIndex: number = stateData.rucioLocations.findIndex(
      (findItem) =>
        findItem.namespace === itemFilter.namespace &&
        findItem.filename === itemFilter.filename,
    );

    // find the site in the list of available sites.
    if (itemIndex > -1) {
      siteAvailable =
        stateData.rucioLocations[itemIndex].availableSites.findIndex(
          (findSite) => findSite.site_uuid === args.site.site_uuid,
        ) > -1;
    }

    return siteAvailable;
    //
  });

  // return something.
  return items.length;
  //
} // fnD_countAvailableFiles

//  ------------------------------------------------------------
//
//  get the Rucio locations for the supplied list of filenames
//  and namespaces.
//
//  ------------------------------------------------------------

export const fnD_getAvailableLocations = createAsyncThunk(
  'data/getAvailableLocations',
  async (
    args: {
      items: DataItemType[];
      useAsync?: boolean;
    },
    thunkAPI,
  ) => {
    //
    // get storage locations.
    let rucioLocations: {
      identifier: string;
      associated_storage_area_id: string;
      replicas: string[];
    }[] = [];

    try {
      //
      let urlCommand: string = apiPrefix() + '/v1/data_management/locate_data';

      if (args.useAsync !== true && args.items.length === 1) {
        //
        urlCommand =
          urlCommand +
          '?namespace=' +
          encodeURIComponent(args.items[0].namespace) +
          '&name=' +
          encodeURIComponent(args.items[0].filename);

        const apiResult = await fetch(urlCommand, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (apiResult.status === 200) {
          //
          const returnedJson = await apiResult.json();

          if (returnedJson.locations !== undefined) {
            rucioLocations = returnedJson.locations;
          }
          //
        }
        //
      } else {
        //
        const apiResult = await fetch(urlCommand, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(args.items),
          credentials: 'include',
        });
        //
      }
      //
    } catch (e) {
      console.log(e);
    }

    // return the storage areas.
    return rucioLocations;
    //
  },
  //
); // fnD_getAvailableLocations

//  ------------------------------------------------------------
//
//  An asynchronous function that loads a list of compute
//  jobs from the backend database.
//
//  ------------------------------------------------------------

export const fnD_getComputeJobs = createAsyncThunk(
  'data/getComputeJobs',
  async (_, thunkAPI: any) => {
    //
    try {
      thunkAPI.dispatch(rD_setComputeJobsLoading(true));
      const jobs = await fetchComputeJobs();
      thunkAPI.dispatch(rD_setComputeJobs(jobs));
    } catch (error) {
      console.error('Failed to load compute jobs:', error);
      thunkAPI.dispatch(rD_setComputeJobs([]));
    } finally {
      thunkAPI.dispatch(rD_setComputeJobsLoading(false));
    }
    //
  },
  //
); // fnD_getComputeJobs

//  ------------------------------------------------------------
//
//  An asynchronous function that loads a list of storage
//  areas from the site-capabilities API.
//
//  ------------------------------------------------------------

export const fnD_getStorageAreas = createAsyncThunk(
  'data/getStorageAreas',
  async (_, thunkAPI: any) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const stateData: DataState = stateFetch.data;

    const url = `${apiPrefix()}/v1/site_capabilities/list_storage`;

    try {
      //
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (res.status === 200) {
        //
        const json = await res.json();

        // Normalize + filter
        const raw: any[] = json?.storage ?? [];
        const filtered = raw.filter((s) => (s.storage_areas?.length ?? 0) > 0);

        const normalized: SiteStorageArea[] = filtered.map((s: any) => ({
          site: stateData.sites.find(
            (find) => find.site_uuid === String(s.site_uuid).toLowerCase(),
          ) ?? { node: '', site: '', site_uuid: '' },
          storage_areas: s.storage_areas,
        }));

        thunkAPI.dispatch(rD_setStorageAreas(normalized));
        //
      } else if (res.status === 401) {
        console.log('Session expired while loading storage areas.');
        thunkAPI.dispatch(rD_setStorageAreas([]));
      } else {
        thunkAPI.dispatch(rD_setStorageAreas([]));
      }
      //
    } catch (e: any) {
      thunkAPI.dispatch(rD_setStorageAreas([]));
    }
    //
  },
  //
); // fnD_getStorageAreas

//  ------------------------------------------------------------
//
//  Get an unique list of service types from the site-
//  capabilities catalog.
//
//  ------------------------------------------------------------

export async function fnD_getServiceTypes() {
  //
  var urlCommand: string = apiPrefix() + '/v1/site_capabilities/list_service_types';

  try {
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();

      // get services list.
      var serviceTypesList: any = [];
      if (returnedJson.services !== undefined) {
        serviceTypesList = returnedJson.services;
      }

      // update the state with the list of returned service types.
      store.dispatch(rD_setServiceTypes(serviceTypesList));
      //
    }
  } catch (e) {}
  //
} // fnD_getServiceTypes

//  ------------------------------------------------------------
//
//  Loop through the list of storage areas, and get the
//  site of the storage ID provided.
//
//  ------------------------------------------------------------

export function fnD_getStorageLocationSite(args: { id: string }) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  const site: Site = stateData.storageAreas.find(
    (siteStorage) =>
      siteStorage.storage_areas.find(
        (storageArea) => storageArea.storage_id === args.id,
      ) !== undefined,
  )?.site ?? {
    node: '',
    site: '',
    site_uuid: '',
  };

  // return something.
  return site;
  //
} // fnD_getStorageLocationSite

//  -------------------------------------------------
//
//  receive a list of data locations as storage ids,
//  convert those ids into sites, and update the
//  available locations in the Rucio object.
//
//  -------------------------------------------------

export function fnD_updateDataLocations(args: {
  dataLocationItem: {
    namespace: string;
    filename: string;
    locations: {
      identifier: string;
      associated_storage_area_id: string;
      replicas: string[];
    }[];
  };
}) {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // get a list of sites where this item exists.
  let availableSites: Site[] = stateData.storageAreas
    .filter((filterSite: SiteStorageArea) => {
      //
      let foundMatch: boolean = false;
      let storageAreas: StorageArea[] = filterSite.storage_areas.filter(
        (filterStorageArea: StorageArea) =>
          args.dataLocationItem.locations.findIndex(
            (findStorageArea) =>
              findStorageArea.associated_storage_area_id === filterStorageArea.storage_id,
          ) > -1,
      );
      return storageAreas.length > 0;
      //
    })
    .map((site) => {
      //
      return site.site;
      //
    });

  // prepare the item to be updated.
  const item: {
    namespace: string;
    filename: string;
    refresh?: boolean;
    availableSites?: Site[];
    movingSites?: Site[];
    refreshingLocations?: boolean;
  } = {
    namespace: args.dataLocationItem.namespace,
    filename: args.dataLocationItem.filename,
    availableSites: availableSites,
    refreshingLocations: false,
  };

  // update state.
  store.dispatch(
    rD_updateRucioLocations({
      items: [item],
    }),
  );
  //
} // fnD_updateDataLocations

//  -------------------------------------------------
//
//  Check whether any of the given items have an
//  accessible namespace (i.e. userCanAccessNamespace
//  is not explicitly false). Returns false for empty
//  item lists. Items not yet checked are treated as
//  accessible (optimistic).
//
//  -------------------------------------------------

export function fnD_hasAccessibleItems(args: {
  items: { namespace: string; filename: string }[];
}): boolean {
  //
  if (args.items.length === 0) {
    return false;
  }

  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  // optimistic: undefined (not yet checked) is treated as accessible.
  return args.items.some((item) => {
    const location: RucioLocationsType | undefined = stateData.rucioLocations.find(
      (r) => r.namespace === item.namespace && r.filename === item.filename,
    );
    return location?.userCanAccessNamespace !== false;
  });
  //
} // fnD_hasAccessibleItems

//  -------------------------------------------------
//
//  Build a MoveDataToStorage payload for auto-stage:
//  every rucioLocations item tagged with `catalog`,
//  targeted at the supplied site's first (default)
//  storage area. Returns null when the catalog is
//  empty or the site has no storage area configured.
//
//  Default lifetime is 3600s (1 hour), matching the
//  existing manual staging path.
//
//  -------------------------------------------------

const AUTO_STAGE_LIFETIME_SECONDS = 3600;

export function fnD_buildMoveDataToStorage(args: {
  catalog: string;
  toSiteUUID: string;
  skahaSessionId?: string;
}): MoveDataToStorage | null {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  const items: RucioLocationsType[] = stateData.rucioLocations.filter((item) =>
    item.catalog.includes(args.catalog),
  );
  if (items.length === 0) return null;

  const siteEntry: SiteStorageArea | undefined = stateData.storageAreas.find(
    (entry) => entry.site.site_uuid === args.toSiteUUID,
  );
  if (siteEntry === undefined || siteEntry.storage_areas.length === 0) return null;

  // Reject site normalized with empty UUID rather than send
  // a malformed staging request.
  const siteUUID: string | undefined = siteEntry.site.site_uuid;
  if (!siteUUID) return null;

  // Auto-stage has no UI to pick a storage area, so default to the first one.
  const storageArea: StorageArea = siteEntry.storage_areas[0];

  const filesToMove: DataItemType[] = items.map((item) => ({
    namespace: item.namespace,
    filename: item.filename,
    did_type: 'FILE',
    bytes: item.bytes ?? 0,
  }));

  return {
    toStorageAreaUUID: storageArea.storage_id,
    toSiteUUID: siteUUID,
    toStorageIdentifier: storageArea.name,
    lifetime: AUTO_STAGE_LIFETIME_SECONDS,
    filesToMove: filesToMove,
    // Temporary session id for the direct (STANDARD) launch; undefined for the
    // dialog launch, in which case no canfar_session_id is sent.
    skahaSessionId: args.skahaSessionId,
    // Opt in to the failure toast (manual staging callers don't).
    notifyOnError: true,
  };
  //
} // fnD_buildMoveDataToStorage

//  ------------------------------------------------------------
//
//  Check whether the user has access to the supplied
//  namespaces. Results arrive via SSE 'namespace_access'
//  events and are merged into rucioLocations state.
//
//  ------------------------------------------------------------

export const fnD_checkIfUserCanAccessNamespaces = createAsyncThunk(
  'data/checkIfUserCanAccessNamespaces',
  async (
    args: {
      items: string[];
      useAsync?: boolean;
      signal?: AbortSignal;
    },
    thunkAPI,
  ) => {
    try {
      const urlCommand: string =
        apiPrefix() + '/v1/data_management/check_namespace_access';

      const response = await fetch(urlCommand, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespaces: args.items }),
        credentials: 'include',
        signal: args.signal,
      });

      if (!response.ok) {
        return thunkAPI.rejectWithValue({
          status: response.status,
          statusText: response.statusText,
        });
      }

      return { success: true };
    } catch (e) {
      console.error('Failed to check namespace access:', e);
      return thunkAPI.rejectWithValue({
        error: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  },
  //
); // fnD_checkIfUserCanAccessNamespaces

//  -------------------------------------------------
//
//  load the list of projects that this user is
//  a member of.
//
//  -------------------------------------------------

export async function fnD_loadProjects() {
  //
  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  try {
    //
    store.dispatch(setLoadingProjects(true));
    const apiResult = await fetch(apiPrefix() + '/v1/projects', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();
      const projects: Project[] | undefined = returnedJson.projects;
      if (projects !== undefined) {
        //
        // set state.
        projects.forEach((item) => {
          //
          if (item.description === null) {
            item.description = undefined;
          }
          if (item.abbreviation === null) {
            item.abbreviation = undefined;
          }
          if (item.icon === null) {
            item.icon = undefined;
          }
          if (item.site_uuid === null) {
            item.site_uuid = undefined;
          }
          //
        });
        store.dispatch(rD_setProjects(projects));

        // see if the session has a currently-selected project.
        const projectID: {} = JSON.parse(sessionStorage.getItem('project_id') ?? '{}');

        if ('id' in projectID) {
          const project: Project | undefined = projects.find(
            (item) => item.id === projectID.id,
          );
          if (project !== undefined) {
            store.dispatch(rG_setProject(project));
          }
        }
        //
      }
      //
    }
  } catch (e) {}
  store.dispatch(setLoadingProjects(false));
  //
} // fnD_loadProjects

//  ------------------------------------------------------------
//
//  An asynchronous function that loads a list of sites from
//  the site-capabilities API.
//
//  ------------------------------------------------------------

export async function fnD_loadSites() {
  //
  var urlCommand: string = apiPrefix() + '/v1/site_capabilities/list_sites';
  const stateFetch: any = store.getState();
  const state: DataState = stateFetch.data;

  try {
    store.dispatch(setLoadingSites(true));
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      //
      const nodesAndSites = await apiResult.json();
      let sites: Site[] = [];
      for (let node: number = 0; node < nodesAndSites.length; node++) {
        for (let site: number = 0; site < nodesAndSites[node].sites.length; site++) {
          const newSite: Site = {
            node: nodesAndSites[node].node.toUpperCase(),
            site: nodesAndSites[node].sites[site].site.toUpperCase(),
            site_uuid: nodesAndSites[node].sites[site].site_uuid.toLowerCase(),
            icon: nodesAndSites[node].sites[site].icon,
            iconBW: nodesAndSites[node].sites[site].icon,
            index: nodesAndSites[node].sites[site].id,
            admin: nodesAndSites[node].sites[site].admin,
          };
          sites.push(newSite);
        }
      }

      // update the state.
      store.dispatch(rD_setSites(sites));

      // see if the session has a currently-selected site.
      const siteUUID: string = sessionStorage.getItem('site_uuid') ?? '';

      const site: Site | undefined = sites.find((item) => item.site_uuid === siteUUID);

      if (site !== undefined) {
        store.dispatch(rG_setSite(site));
      }
      //
    } else {
      store.dispatch(rD_setSites([]));
    }
    store.dispatch(setLoadingSites(false));
  } catch (e) {
    store.dispatch(setLoadingSites(false));
  }
  //
} // fnD_loadSites

//  ------------------------------------------------------------
//
//  An asynchronous function that updates the details of a
//  project.
//
//  ------------------------------------------------------------

export async function fnD_updateProject(args: { project: Project; site?: Site }) {
  //
  let runUpdate: boolean = false;
  let urlCommand: string =
    apiPrefix() +
    '/v1/projects' +
    '?project_name=' +
    encodeURIComponent(args.project.name.toLowerCase());
  if (args.site !== undefined) {
    urlCommand = urlCommand + '&site_uuid=' + args.site.site_uuid ?? '';
    runUpdate = runUpdate || (args.site.site_uuid ?? '') !== '';
  }

  if (runUpdate === true) {
    try {
      //
      const apiResult = await fetch(urlCommand, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        //
      }
      //
    } catch (e) {}
  }
  //
} // fnD_updateProject
